import { action, computed, makeObservable, observable } from 'mobx';

import { CashbackInfo, ClaimCashbackPayload, HTTPResponse } from '../models';
import { cashbackController, HttpResponse, launcherSocket } from '../api';
import { dotFormatter, setTranslationValues } from '../helpers';
import { FIVE_MINUTES, T } from '../constants';
import { RootStore } from '.';

export class CashbackStore {

  private rootStore: RootStore;

  public animateTrigger = false;

  public showCashback = false;

  public cashbackInfo = new HttpResponse<CashbackInfo | null>(null);

  public announcementSmallView: boolean = false;

  public announcementModalOpen: boolean = false;

  private claimReadyReminderTimeout?: NodeJS.Timeout;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      cashbackInfo: observable,
      setCashbackInfo: action.bound,

      animateTrigger: observable,
      toggleAnimateTrigger: action.bound,

      showCashback: observable,
      setShowCashback: action.bound,

      announcementSmallView: observable,
      setAnnouncementSmallView: action.bound,

      announcementModalOpen: observable,
      setAnnouncementModalOpen: action.bound,

      cashbackCurrencySymbol: computed
    });
  }

  public get cashbackCurrencySymbol(): string {
    return this.rootStore.gameStore.currencySymbol;
  }

  public setCashbackInfo(value: HttpResponse<CashbackInfo | null>): void {
    this.cashbackInfo = value;
  }

  public toggleAnimateTrigger(): void {
    this.animateTrigger = !this.animateTrigger;
  }

  public setShowCashback(value: boolean): void {
    this.showCashback = value;
  }

  public setAnnouncementSmallView(value: boolean): void {
    this.announcementSmallView = value;
  }

  public setAnnouncementModalOpen(value: boolean): void {
    this.announcementModalOpen = value;
  }

  public async getCashbackInfo(gameId: number): Promise<void> {
    this.setCashbackInfo(this.cashbackInfo.fetching());
    const data = await cashbackController.getCashbackInfo(gameId);
    this.setCashbackInfo(this.cashbackInfo.fetched(data));
  }

  public async claimCashback(payload: ClaimCashbackPayload): Promise<HTTPResponse> {
    return await cashbackController.claimCashback(payload);
  }

  private clearClaimReadyReminder(): void {
    if (this.claimReadyReminderTimeout) {
      clearTimeout(this.claimReadyReminderTimeout);
      this.claimReadyReminderTimeout = undefined;
    }
  }

  private scheduleClaimReadyReminder(levels: CashbackInfo['levels'] = []): void {
    const claimableLevels = levels.filter(({ isCompleted, isClaimed }) => isCompleted && !isClaimed);
    if (!claimableLevels.length) {
      this.clearClaimReadyReminder();
      return;
    }

    const lastClaimableLevel = claimableLevels.reduce((latest, level) => {
      return level.level > latest.level ? level : latest;
    });

    this.clearClaimReadyReminder();

    this.claimReadyReminderTimeout = setTimeout(() => {
      const currentLevels = this.cashbackInfo.data?.levels || [];
      const isStillClaimable = currentLevels.some(({ id, isCompleted, isClaimed }) => {
        return id === lastClaimableLevel.id && isCompleted && !isClaimed;
      });

      if (isStillClaimable) {
        const { currencySymbol } = this.rootStore.gameStore;
        this.rootStore.rewardsNotificationStore.enqueue(
          setTranslationValues(
            this.rootStore.translationsStore.translate(T.REWARDS_PRIZE_READY_NOTIFICATION),
            {
              level: lastClaimableLevel.level,
              prize: `${currencySymbol}${dotFormatter(lastClaimableLevel.rewardAmount)}`
            }
          )
        );
      }

      this.claimReadyReminderTimeout = undefined;
    }, FIVE_MINUTES);
  }

  private handleCashbackUpdateNotifications(data: Partial<CashbackInfo>): void {
    if (!this.cashbackInfo.data) return;

    const { progress: oldProgress } = this.cashbackInfo.data;
    const { progress: newProgress, currentLevel: newCurrentLevel } = data;
    const levels = this.cashbackInfo.data.levels || [];
    const oldClaimables = levels.filter(({ isCompleted, isClaimed }) => isCompleted && !isClaimed).length || 0;
    const newClaimables = data.levels?.filter(({ isCompleted, isClaimed }) => isCompleted && !isClaimed).length || 0;
    const hasNewClaim = newClaimables > oldClaimables;

    const { currencySymbol } = this.rootStore.gameStore;
    const rewardAmount = levels.find(({ level }) => level === newCurrentLevel)?.rewardAmount || 0;
    const passedRewardAmount = newCurrentLevel && newCurrentLevel > 1 ? levels.find(({ level }) => level === newCurrentLevel - 1)?.rewardAmount || 0 : 0;

    let trKey = '';
    let trVariables = { prize: `${currencySymbol}${dotFormatter(rewardAmount)}` };

    if (hasNewClaim) {
      trKey = T.REWARDS_LEVEL_UP_NOTIFICATION;

      const levelCount = data.levelCount ?? this.cashbackInfo.data.levelCount;
      // Check if the last level was explicitly completed in the incoming data updates
      const isLastLevelCompleted = data.levels?.find(({ level }) => level === levelCount)?.isCompleted;

      if (newCurrentLevel === levelCount && isLastLevelCompleted) {
        // Passed the last level completely -> Show its reward amount
        trVariables = { prize: `${currencySymbol}${dotFormatter(rewardAmount)}` };
      } else {
        // Passed an intermediate level -> Show the previous level's reward amount
        trVariables = { prize: `${currencySymbol}${dotFormatter(passedRewardAmount)}` };
      }

      this.scheduleClaimReadyReminder(data.levels || levels);
    } else if (newProgress && newProgress >= 90 && oldProgress < 90) {
      trKey = T.REWARDS_NOTIFICATION_90;
    } else if (newProgress && newProgress >= 50 && oldProgress < 50) {
      trKey = T.REWARDS_NOTIFICATION_50;
    }
    if (trKey) {
      this.rootStore.rewardsNotificationStore.enqueue(
        setTranslationValues(
          this.rootStore.translationsStore.translate(trKey),
          trVariables
        )
      );
    }
  }

  public handleCashbackUpdate(data: Partial<CashbackInfo>): void {
    if (!this.cashbackInfo.data || this.cashbackInfo.data.id !== data.id) return;
    this.handleCashbackUpdateNotifications(data);
    const oldData = this.cashbackInfo.data;
    this.setCashbackInfo(
      this.cashbackInfo.fetched({
        ...oldData,
        ...data,
        levels: oldData.levels.map((level) => {
          const newLevel = data.levels?.find(({ id }) => id === level.id);
          return {
            ...level,
            ...newLevel
          }
        })
      })
    );
  }

  public handleCashbackStarted(): void {
    this.setAnnouncementSmallView(true);
    this.setShowCashback(true);
  }

  public handleCashbackEnded(id: number): void {
    if (!this.cashbackInfo.data) return;
    if (id === this.cashbackInfo.data.id) {
      this.clearClaimReadyReminder();
      this.setShowCashback(false);
    }
  }

  public listenEvents(): void {
    launcherSocket.onCashbackStarted(this.handleCashbackStarted.bind(this));
    launcherSocket.onCashbackUpdate(this.handleCashbackUpdate.bind(this));
    launcherSocket.onCashbackEnded(this.handleCashbackEnded.bind(this));
  }

}
